// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useMemo } from 'react';

import type { SavedGridStateJsonString, TableViewId, TableViewType } from '@evolvemep/api-ts';

import type { TableSessionState } from 'helpers/ag-grid/TableViewPanel/types';
import { gridStatesEqual } from 'helpers/ag-grid/TableViewPanel/utils';
import { isNil } from 'helpers/isNotNil';
import { getLocalStorageWrapped, useLocalStorage } from 'hooks/useLocalStorage';

const emptyTableSessionState: TableSessionState = { selectedTableViewId: null };

/** Reads the session state outside of React's render cycle, e.g. in grid event handlers and async callbacks. */
export const getTableSessionState = (tableViewType: TableViewType) =>
  getLocalStorageWrapped(`TABLE_SESSION_STATE_${tableViewType}`);

/**
 * The user's selected table view and unsaved grid changes for a table view type.
 *
 * Stored alongside the user's auth tokens, so it lasts exactly as long as their session.
 */
export const useTableSessionState = (tableViewType: TableViewType) => {
  const [tableSessionState, setTableSessionState] = useLocalStorage(`TABLE_SESSION_STATE_${tableViewType}`, undefined);

  const updateTableSessionState = useCallback(
    (update: (previous: TableSessionState) => TableSessionState) =>
      setTableSessionState((previous) => update(previous ?? emptyTableSessionState)),
    [setTableSessionState],
  );

  const selectedTableViewId = tableSessionState?.selectedTableViewId ?? null;

  /** Selects a table view whose saved grid state hasn't been fetched yet. */
  const setSelectedTableViewId = useCallback(
    (tableViewId: TableViewId | null) =>
      updateTableSessionState(({ gridState }) => ({ selectedTableViewId: tableViewId, gridState })),
    [updateTableSessionState],
  );

  /** Records that a table view's saved grid state has just been applied to the grid, discarding any unsaved changes. */
  const recordAppliedTableView = useCallback(
    (tableViewId: TableViewId, savedGridState: SavedGridStateJsonString) =>
      setTableSessionState({ selectedTableViewId: tableViewId, savedGridState, gridState: savedGridState }),
    [setTableSessionState],
  );

  const recordGridState = useCallback(
    (gridState: SavedGridStateJsonString) => updateTableSessionState((previous) => ({ ...previous, gridState })),
    [updateTableSessionState],
  );

  /** Updates the selected table view's saved grid state without touching the user's unsaved changes. */
  const recordLatestSavedGridState = useCallback(
    (tableViewId: TableViewId, savedGridState: SavedGridStateJsonString) =>
      updateTableSessionState((previous) =>
        previous.selectedTableViewId === tableViewId ? { ...previous, savedGridState } : previous,
      ),
    [updateTableSessionState],
  );

  const onTableViewDeleted = useCallback(
    (tableViewId: TableViewId) =>
      updateTableSessionState((previous) =>
        previous.selectedTableViewId === tableViewId
          ? { selectedTableViewId: null, gridState: previous.gridState }
          : previous,
      ),
    [updateTableSessionState],
  );

  /** Deselects the table view and discards all unsaved changes. */
  const resetTableSessionState = useCallback(
    () => setTableSessionState(emptyTableSessionState),
    [setTableSessionState],
  );

  /** Forgets the session entirely, so the table starts from the user's default table view the next time it loads. */
  const clearTableSessionState = useCallback(() => setTableSessionState(undefined), [setTableSessionState]);

  const selectedTableViewModified = useMemo(() => {
    const { savedGridState, gridState } = tableSessionState ?? {};
    if (isNil(savedGridState) || isNil(gridState)) return false;
    return !gridStatesEqual(gridState, savedGridState);
  }, [tableSessionState]);

  return {
    selectedTableViewId,
    setSelectedTableViewId,
    selectedTableViewModified,
    recordAppliedTableView,
    recordGridState,
    recordLatestSavedGridState,
    onTableViewDeleted,
    resetTableSessionState,
    clearTableSessionState,
  };
};
